import { useState } from "react";
import RelatedRecordsDrawer from "./RelatedRecordsDrawer";
import AppPageHeader from "../../../components/Headers/AppPageHeader";
import { modulesAtom, tenantIDAtom } from "../../../atoms/GlobalAtoms";
import { useAtomValue } from "jotai";
import { IsMobileAtom } from "../../../App";
import { SelectPicker } from "rsuite";
import { Button, Container } from "reactstrap";
import useCloud from "../../../api/api-hooks/cloud/useCloud";
import BasicTableView from "../../supportal/CloudListView/BasicTableView";
import IconButton from "../../../elements/IconButton";
import useUpdateCloud from "../../../api/api-hooks/cloud/useUpdateCloud";
import { RelatedRecord } from "../../../types/GlobalInterfaces";
import { findIndex } from "underscore";
import AppHelmet from "../../../components/AppComponents/AppHelmet";

const ModulesToHide = [
  "Feed",
  "InstalledApps",
  "FileImports",
  "Webhooks",
  "PermissionProfiles",
  "Pages",
  "Reports",
  "ReportLogs",
  "Workspaces",
  "Workflows",
  "CloudBackups",
  "Tags",
  "Files",
];

const RelatedRecords = () => {
  const tenantID = useAtomValue(tenantIDAtom);
  const mobileScreen = useAtomValue(IsMobileAtom);
  const { cloud, cloudLoading } = useCloud(tenantID ? tenantID : "");
  const existingRecords = cloud?.RelatedRecords || [];
  const { updateCloud, updatingCloud, updateCloudError } = useUpdateCloud();
  const modulesState = useAtomValue(modulesAtom);
  const modules = (modulesState || [])?.filter(
    (mod) => !ModulesToHide.includes(mod.APIName)
  );
  const [openDrawer, setOpenDrawer] = useState(false);
  const [selectedModule, setSelectedModule] = useState<string | undefined>();
  const [selectedRecord, setSelectedRecord] = useState<
    RelatedRecord | undefined
  >();

  // The remaining modules (Only one Cloud Related Record per module.)
  const remainingModuleOptions = modules.filter((mod) => {
    if (
      existingRecords.length < 1 ||
      selectedRecord?._ModuleAPIName === mod.APIName
    ) {
      return true;
    }

    const match = existingRecords.find((relatedRecord) => {
      return relatedRecord._ModuleAPIName === mod.APIName;
    });

    return match ? false : true;
  });

  return (
    <>
      <AppHelmet title="Cloud Defaults - GrowthCloud.com" />
      <Container fluid className="p-0">
        <AppPageHeader
          title="Cloud Defaults "
          size="sm"
          className="py-3"
          description="Manage all cloud defaults"
          customContent={
            <div
              className="d-flex justify-content-between align-items-center"
              style={{ flexWrap: "wrap" }}
            >
              {!!modules?.length && (
                <div
                  className={`${
                    mobileScreen ? "mt-3" : ""
                  } d-flex align-items-center mr-3`}
                >
                  <i
                    className="mdi mdi-database mr-1"
                    style={{
                      fontSize: 24,
                    }}
                  />
                  <SelectPicker
                    data={modules}
                    placeholder="Data Source"
                    labelKey="Name"
                    valueKey="APIName"
                    value={selectedModule}
                    searchable={modules.length > 5}
                    cleanable={!!selectedModule}
                    onChange={(moduleAPIName: string | null) =>
                      setSelectedModule(moduleAPIName || undefined)
                    }
                    style={{ minWidth: 200 }}
                  />
                </div>
              )}
              <div className={`${mobileScreen ? "mt-3" : ""}`}>
                <Button
                  className="ml-2"
                  color="primary"
                  onClick={() => setOpenDrawer(true)}
                >
                  Create
                </Button>
              </div>
            </div>
          }
        />
        <BasicTableView
          loading={cloudLoading}
          items={existingRecords}
          title={"Name"}
          getItemTitle={(relatedRecord) => relatedRecord._RecordName}
          getItemSubtitle={(relatedRecord) => relatedRecord._ModuleAPIName}
          CustomRenderer={(relatedRecord) => {
            return (
              <div>
                <IconButton
                  icon="pencil"
                  tooltipText="Edit Cloud Related Record"
                  onClick={() => handleEditCloudRelatedRecord(relatedRecord)}
                />
                <IconButton
                  icon="close"
                  tooltipText="Delete Cloud Related Record"
                  onClick={() => handleDeleteCloudRelatedRecord(relatedRecord)}
                  color="danger"
                />
              </div>
            );
          }}
          emptyMessage={"No cloud defaults have been added."}
        />
        <RelatedRecordsDrawer
          open={openDrawer}
          handleClose={handleCloseDrawer}
          handleSave={(existingRelatedRecord: RelatedRecord) =>
            handleSave(existingRelatedRecord)
          }
          updatingCloud={updatingCloud}
          existingRelatedRecord={selectedRecord}
          setExistingRelatedRecord={setSelectedRecord}
          remainingModuleOptions={remainingModuleOptions}
        />
      </Container>
    </>
  );

  function handleSave(newRelatedRecord: RelatedRecord) {
    const payload: [] | RelatedRecord[] = existingRecords;

    if (
      !tenantID ||
      !newRelatedRecord._ModuleAPIName ||
      !newRelatedRecord.id ||
      !newRelatedRecord._id ||
      !newRelatedRecord.Value
    ) {
      return;
    }

    const index = findIndex(payload, {
      _ModuleAPIName: newRelatedRecord._ModuleAPIName,
    });

    if (index !== -1) {
      payload[index] = newRelatedRecord;
    } else {
      payload.push(newRelatedRecord);
    }

    setSelectedRecord(undefined);
    updateCloud({
      tenantID,
      payload: { RelatedRecords: payload },
      onSuccess: () => setOpenDrawer(false),
    });
  }

  function handleEditCloudRelatedRecord(relatedRecord: RelatedRecord) {
    setSelectedRecord(relatedRecord);

    setOpenDrawer(true);
  }

  function handleDeleteCloudRelatedRecord(relatedRecord: RelatedRecord) {
    let payload: [] | RelatedRecord[] = existingRecords;

    if (!relatedRecord || !tenantID) return;

    payload = payload.filter((item) => item.id !== relatedRecord.id);

    updateCloud({ tenantID, payload: { RelatedRecords: payload } });
  }

  function handleCloseDrawer() {
    setOpenDrawer(false);
    setSelectedRecord(undefined);
  }
};

export default RelatedRecords;
